import { useState, useEffect } from "react";
import { questions } from "@/data/questions";

export type AssessmentStatus = "not_started" | "in_progress" | "completed";

export interface AssessmentState {
  status: AssessmentStatus;
  userName: string | null;
  progress: number; // 0–100
  currentIndex: number;
}

const readAssessmentState = (): AssessmentState => {
  const answersRaw = localStorage.getItem("careerGPS_answers");
  const userName = localStorage.getItem("careerGPS_userName");
  const completed = localStorage.getItem("careerGPS_completed") === "true";

  if (!answersRaw) {
    return { status: "not_started", userName, progress: 0, currentIndex: 0 };
  }

  try {
    const answers = JSON.parse(answersRaw) as Record<string, number[]>;
    const answeredCount = Object.keys(answers).length;
    const total = questions.length;
    const progress = Math.round((answeredCount / total) * 100);

    if (completed) {
      return { status: "completed", userName, progress: 100, currentIndex: total };
    }
    if (answeredCount > 0 && answeredCount < total) {
      return { status: "in_progress", userName, progress, currentIndex: answeredCount };
    }
    if (answeredCount >= total) {
      return { status: "completed", userName, progress: 100, currentIndex: total };
    }
    return { status: "not_started", userName, progress: 0, currentIndex: 0 };
  } catch {
    return { status: "not_started", userName, progress: 0, currentIndex: 0 };
  }
};

export const useAssessmentState = (): AssessmentState => {
  const [state, setState] = useState<AssessmentState>(() => readAssessmentState());

  useEffect(() => {
    const update = () => {
      setState(readAssessmentState());
    };

    update();
    window.addEventListener("storage", update);
    // Custom event for same-tab updates
    window.addEventListener("assessmentStateChanged", update);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener("assessmentStateChanged", update);
    };
  }, []);

  return state;
};
